import { Link } from "react-router-dom";
import { LegalPage } from "@/components/legal/LegalPage";
import { SELLER } from "@/config/legal";

const TermsOfUse = () => (
  <LegalPage title="Terms & Conditions" description="The terms that govern your use of Veylo AI." path="/terms-of-use">
    <section>
      <h2>1. Who we are</h2>
      <p>
        {SELLER.tradingName} ({SELLER.website}) is operated by <strong>{SELLER.legalName}</strong>, a company registered in {SELLER.country}, trading as {SELLER.tradingName} ("we", "us").
        When you use the service or buy a subscription, you are entering into an agreement with {SELLER.legalName}.
      </p>
    </section>
    <section>
      <h2>2. Acceptance</h2>
      <p>By creating an account or continuing to use the service, you agree to these terms. If you do not agree, please do not use the service.</p>
    </section>
    <section>
      <h2>3. The service</h2>
      <p>
        {SELLER.productName} is an AI-powered analytics platform: you upload or enter business data, and the service cleans it,
        produces insights, dashboards, forecasts, reports and automated workflows. Features depend on the plan you choose.
      </p>
    </section>
    <section>
      <h2>4. Your account</h2>
      <ul>
        <li>You must be of legal age, or have authority to bind the organisation you sign up for.</li>
        <li>Keep your login details confidential; you are responsible for activity under your account.</li>
        <li>Provide accurate information and keep it up to date.</li>
      </ul>
    </section>
    <section>
      <h2>5. Acceptable use</h2>
      <p>You must not misuse the service, including:</p>
      <ul>
        <li>using it for anything unlawful, fraudulent, or to send spam;</li>
        <li>uploading content that infringes others' intellectual property or privacy;</li>
        <li>interfering with security — malware, probing, scraping, or overloading the service;</li>
        <li>reverse engineering, reselling or redistributing the service, or bypassing plan limits;</li>
        <li>using the AI features to create illegal, hateful, deceptive (e.g. deepfake) or harmful material, or trying to jailbreak them.</li>
      </ul>
    </section>
    <section>
      <h2>6. AI features and your content</h2>
      <ul>
        <li>You keep ownership of the data and prompts you provide, and of the outputs generated for you, to the extent the law allows.</li>
        <li>You are responsible for having the rights to what you upload and for how you use the outputs.</li>
        <li>You give us a limited licence to host and process your content only to provide the service.</li>
        <li>AI output can be inaccurate or incomplete. Check important results; it is not professional financial, legal or tax advice.</li>
        <li>We may remove or restrict content, refuse outputs, or suspend accounts that break these terms. Rights-holders can report infringement to {SELLER.supportEmail}; repeat infringers will lose access.</li>
      </ul>
    </section>
    <section>
      <h2>7. Intellectual property</h2>
      <p>{SELLER.legalName} owns the service, its software, documentation and branding. We grant you a limited, non-exclusive, non-transferable right to use it within your plan.</p>
    </section>
    <section>
      <h2>8. Payments and subscriptions</h2>
      <p>
        Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders.
        Paddle provides all customer service inquiries and handles returns.
      </p>
      <p className="mt-2">
        Payment, billing, renewal, taxes and cancellation are governed by{" "}
        <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noopener noreferrer">Paddle's Buyer Terms</a>.
        Subscriptions renew monthly until cancelled; you can cancel any time from the Billing page. Refunds are covered in our{" "}
        <Link to="/refund-policy">Refund Policy</Link>.
      </p>
    </section>
    <section>
      <h2>9. Service level</h2>
      <p>We work to keep the service reliable, but we do not guarantee it will be uninterrupted or error-free. It is provided "as is", and implied warranties are excluded to the fullest extent permitted by law.</p>
    </section>
    <section>
      <h2>10. Suspension and termination</h2>
      <p>We may suspend or end your access for material breach of these terms, non-payment, security or fraud risk, or repeated or serious policy violations. You may close your account at any time. After closure you can export your data for 30 days, after which it is deleted.</p>
    </section>
    <section>
      <h2>11. Liability</h2>
      <p>
        We are not liable for indirect or consequential losses (such as lost profits, data or goodwill). Our total liability is limited to the fees you paid in the 12 months before the claim.
        Nothing here limits liability for fraud, death or personal injury where the law does not allow it. You agree to cover claims arising from your content or your breach of these terms.
      </p>
    </section>
    <section>
      <h2>12. General</h2>
      <p>These terms are governed by the laws of {SELLER.country}, and its courts have jurisdiction. We are not responsible for delays caused by events beyond our reasonable control. You may not transfer your account without our consent. We may update these terms and will notify you of material changes.</p>
    </section>
    <section>
      <h2>13. Contact</h2>
      <p>Questions: <a href={`mailto:${SELLER.supportEmail}`}>{SELLER.supportEmail}</a></p>
    </section>
  </LegalPage>
);

export default TermsOfUse;
